import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { showConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { deleteCategory, listCategories, saveCategory } from '../../data/templates';
import { describeApiError } from '../../data/http';
import type { CategorySummary } from '../../data/types';

const blank: CategorySummary = { id: '', name: '', sortOrder: 0, code: '' };

const field =
  'mt-1 block w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

/** TA-13: categories were a C# enum, so adding one meant a redeploy. */
export function CategoriesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [draft, setDraft] = useState<CategorySummary>(blank);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () =>
      listCategories()
        .then((saved) => {
          setCategories(saved);
          setError(null);
        })
        .catch((err: unknown) => setError(describeApiError(err, t('errors.loadTemplates')))),
    [t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    // The id is the row key, so an author never has to invent one.
    const id = (draft.id || draft.name).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const code = draft.code.trim().toUpperCase();
    if (id.length === 0 || draft.name.trim().length === 0 || !/^[A-Z0-9]{2}$/.test(code)) return;

    setBusy(true);
    try {
      await saveCategory({ ...draft, id, name: draft.name.trim(), code });
      toast({ title: t('studio.categories.saved'), type: 'success' });
      setDraft(blank);
      await load();
    } catch (err) {
      toast({ title: describeApiError(err, t('errors.saveTemplate')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (category: CategorySummary) => {
    const confirmed = await showConfirmModal({
      title: t('studio.categories.deleteTitle'),
      description: t('studio.categories.deleteDescription', { name: category.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteCategory(category.id);
      toast({ title: t('studio.categories.deleted'), type: 'success' });
      await load();
    } catch (err) {
      // A category still in use comes back as 409 with the template names.
      toast({ title: describeApiError(err, t('errors.saveTemplate')), type: 'error' });
    }
  };

  return (
    <>
      <Link
        to="/studio/reference"
        className="text-sm font-semibold text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {t('reference.title')}
      </Link>

      <h1 className="mt-3 text-3xl font-heading font-semibold tracking-normal text-foreground">
        {t('studio.categories.title')}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('studio.categories.subtitle')}</p>

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <form onSubmit={submit} className="mt-6 rounded-lg border border-border bg-surface p-4">
        <h2 className="text-base font-semibold text-foreground">{t('studio.categories.addTitle')}</h2>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('studio.categories.name')}</span>
            <input
              className={field}
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              required
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('studio.categories.sortOrder')}</span>
            <input
              type="number"
              className={field}
              value={draft.sortOrder}
              onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })}
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('studio.categories.code')}</span>
            <input
              className={field}
              value={draft.code}
              maxLength={2}
              placeholder={t('studio.categories.codePlaceholder')}
              onChange={(event) => setDraft({ ...draft, code: event.target.value.toUpperCase() })}
              required
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="mt-4 inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          {t('studio.categories.save')}
        </button>
      </form>

      {categories.length > 0 ? (
        <div className="mt-6 overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted">
              <tr>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">
                  {t('studio.categories.name')}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">
                  {t('studio.categories.sortOrder')}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">
                  {t('studio.categories.code')}
                </th>
                <th scope="col" className="px-4 py-2.5 text-right font-semibold text-foreground">
                  {t('library.documents.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {categories.map((category) => (
                <tr key={category.id} className="hover:bg-muted">
                  <td className="px-4 py-3 font-semibold text-foreground">{category.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{category.sortOrder}</td>
                  <td className="px-4 py-3 text-muted-foreground">{category.code}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setDraft(category)}
                        className="rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        {t('settings.system.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(category)}
                        aria-label={t('studio.categories.deleteLabel', { name: category.name })}
                        className="inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-destructive transition hover:bg-destructive-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        <TrashIcon className="size-4" aria-hidden="true" />
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
